import { useContext, useEffect, useMemo } from 'react'
import { useSearchEstablishmentsList } from '@/modules/accredited-establishments/hooks/useSearchEstablishmentsList'
import { makeGetEstablishmentsArgs } from '@/modules/accredited-establishments/functions'
import {
  FiltersContext,
  FiltersDispatchContext
} from '@/modules/accredited-establishments/contexts'
import {
  AddressPayload,
  CoordinatesPayload
} from '@/modules/accredited-establishments/hooks/useFiltersReducer/useFiltersReducer.types'
import { useMediaQuery } from '@/global/hooks'
import {
  DESKTOP_REACH_END_MESSAGE_THRESHOLD,
  MOBILE_REACH_END_MESSAGE_THRESHOLD
} from './useList.constants'

const useList = () => {
  const isMobile = useMediaQuery({ maxWidth: 'mobile' })

  const {
    establishmentListLocation,
    categories,
    plan,
    specialty,
    subSpecialty,
    gender,
    appointmentType,
    providerName,
    ready
  } = useContext(FiltersContext)

  const filtersDispatch = useContext(FiltersDispatchContext)

  const args = makeGetEstablishmentsArgs({
    searchFilters: {
      ...establishmentListLocation,
      categories,
      plan,
      specialty,
      subSpecialty,
      gender,
      appointmentType,
      providerName
    },
    /*
     * Roda tambem na visao de mapa: a resposta traz o `categoryCounts` dos
     * chips de categoria, e a chave de query e a mesma da listagem, entao a
     * volta para a lista reaproveita o cache. A localizacao e a da listagem,
     * que o mapa nao mexe ao arrastar — o contador nao refaz busca por pan.
     */
    enabled: ready,
    hasDefaultSearchProps: false
  })

  const result = useSearchEstablishmentsList({ ...args, toastOnError: false })

  const { data, error, isLoading, isFetched, isFetchingNextPage, hasNextPage } =
    result

  const establishments = useMemo(() => {
    return data?.pages.map(page => page.items).flat() || []
  }, [data])

  const establishmentsCount = useMemo(() => {
    return data?.pages[0].totalItems || 0
  }, [data])

  useEffect(() => {
    if (isMobile && isFetched && establishmentsCount === 0) {
      if (document.activeElement instanceof HTMLElement) {
        document.activeElement.blur()
      }
    }
  }, [isMobile, isFetched, establishmentsCount])

  const hasResult = establishments.length > 0
  /* `isFetched`, not `!isLoading`: the list still shows the previous result. */
  const notFoundEstablishments = isFetched && !hasResult && !error

  const itemsLimit = isMobile
    ? MOBILE_REACH_END_MESSAGE_THRESHOLD
    : DESKTOP_REACH_END_MESSAGE_THRESHOLD
  const showReachEndMessage =
    !isLoading &&
    !isFetchingNextPage &&
    !hasNextPage &&
    establishments.length > itemsLimit

  const searchByLatLon = (location: CoordinatesPayload) => {
    filtersDispatch({
      type: 'UPDATE_ESTABLISHMENT_LIST_LOCATION',
      payload: location
    })
  }

  const searchByAddress = (location: AddressPayload) => {
    filtersDispatch({
      type: 'UPDATE_ESTABLISHMENT_LIST_LOCATION',
      payload: location
    })
  }

  return {
    ...result,
    specialty,
    establishments,
    establishmentsCount,
    hasResult,
    notFoundEstablishments,
    showReachEndMessage,
    searchByLatLon,
    searchByAddress
  }
}

export { useList }
